import { type AgentSessionMessageResponse } from '@air/server-sdk';

import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { workspaceAgentMessageShouldInvalidateCaches } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageShouldInvalidateCaches';

export type WorkspaceAgentMessagesById = Record<string, WorkspaceAgentMessage>;

/**
 * Messages are keyed by id and stamped with the session they belong to, so a session switch can
 * never merge the previous conversation's rows into the new one — even when react-query serves the
 * new session's first page from cache in the same commit.
 */
export type WorkspaceAgentMessageStore = {
  byId: WorkspaceAgentMessagesById;
  sessionId: string | null;
};

export const EMPTY_WORKSPACE_AGENT_MESSAGE_STORE: WorkspaceAgentMessageStore = { byId: {}, sessionId: null };

const OPTIMISTIC_ID_PREFIX = 'temp:';

/** Places an optimistic row after the last confirmed message but before the next real sequence. */
const OPTIMISTIC_SEQUENCE_OFFSET = 0.5;

/**
 * Round-tripped through the message's metadata so a confirmed message can be matched back to the
 * optimistic row standing in for it. The server assigns its own id, and content is not unique —
 * sending the same text twice would otherwise leave one row pending forever.
 */
export const WORKSPACE_AGENT_CLIENT_MESSAGE_ID_KEY = 'clientMessageId';

export const getWorkspaceAgentClientMessageId = (metadata: Record<string, unknown> | null): string | undefined => {
  const value = metadata?.[WORKSPACE_AGENT_CLIENT_MESSAGE_ID_KEY];

  return typeof value === 'string' && value ? value : undefined;
};

export const getWorkspaceAgentMessagesForSession = (
  store: WorkspaceAgentMessageStore,
  sessionId: string | null,
): WorkspaceAgentMessagesById => (store.sessionId === sessionId ? store.byId : {});

export const normalizeWorkspaceAgentMessage = (message: AgentSessionMessageResponse): WorkspaceAgentMessage => ({
  id: message.id,
  workspaceId: message.workspaceId,
  sessionId: message.sessionId,
  sequence: message.sequence,
  role: message.role,
  content: message.content ?? null,
  metadata: message.metadata ?? null,
  createdAt: message.createdAt,
  createdBy: message.createdBy,
  isPending: false,
});

export type CreateOptimisticWorkspaceAgentMessageParams = {
  byId: WorkspaceAgentMessagesById;
  clientMessageId: string;
  content: string;
  /** The same metadata being sent, so the optimistic bubble renders its pills rather than monikers. */
  metadata: Record<string, unknown> | undefined;
  /** Null for the message that starts a conversation, which is sent before a session id exists. */
  sessionId: string | null;
  workspaceId: string;
};

export const createOptimisticWorkspaceAgentMessage = ({
  byId,
  clientMessageId,
  content,
  metadata,
  sessionId,
  workspaceId,
}: CreateOptimisticWorkspaceAgentMessageParams): WorkspaceAgentMessage => {
  const lastSequence = Object.values(byId).reduce((highest, entry) => Math.max(highest, entry.sequence), 0);

  return {
    id: `${OPTIMISTIC_ID_PREFIX}${clientMessageId}`,
    workspaceId,
    // Only read when deciding whether an incoming socket message belongs to the session on screen,
    // which an optimistic row is never the subject of. A row created before `start` has answered
    // therefore stands with no session until `stampWorkspaceAgentMessagesSession` gives it one.
    sessionId: sessionId ?? '',
    sequence: lastSequence + OPTIMISTIC_SEQUENCE_OFFSET,
    role: 'user',
    content,
    metadata: { ...metadata, [WORKSPACE_AGENT_CLIENT_MESSAGE_ID_KEY]: clientMessageId },
    createdAt: new Date().toISOString(),
    createdBy: '',
    isPending: true,
  };
};

/**
 * Takes back the row added for a message that turned out not to be sendable. Without this, a failed
 * send would leave a bubble in the thread that never resolves, describing a message the server never
 * received.
 */
export const removeOptimisticWorkspaceAgentMessage = (
  byId: WorkspaceAgentMessagesById,
  clientMessageId: string,
): WorkspaceAgentMessagesById => {
  const { [`${OPTIMISTIC_ID_PREFIX}${clientMessageId}`]: _removed, ...rest } = byId;

  return rest;
};

/**
 * Files rows against the session they turned out to belong to, for the conversation whose first
 * message was written before `start` had answered with an id.
 */
export const stampWorkspaceAgentMessagesSession = (
  byId: WorkspaceAgentMessagesById,
  sessionId: string,
): WorkspaceAgentMessagesById =>
  Object.fromEntries(Object.entries(byId).map(([id, message]) => [id, { ...message, sessionId }]));

/**
 * A confirmed user message supersedes the optimistic row inserted for it. Falls back to matching on
 * trimmed content for messages sent before the client id existed, or by another client entirely.
 */
const dropSupersededOptimisticRow = (
  byId: WorkspaceAgentMessagesById,
  message: WorkspaceAgentMessage,
): WorkspaceAgentMessagesById => {
  if (message.role !== 'user' || message.id.startsWith(OPTIMISTIC_ID_PREFIX)) {
    return byId;
  }

  const clientMessageId = getWorkspaceAgentClientMessageId(message.metadata);
  const pendingRows = Object.values(byId).filter((candidate) => candidate.isPending && candidate.role === 'user');

  const supersededId = clientMessageId
    ? pendingRows.find((candidate) => getWorkspaceAgentClientMessageId(candidate.metadata) === clientMessageId)?.id
    : pendingRows.find((candidate) => (candidate.content ?? '').trim() === (message.content ?? '').trim())?.id;

  if (!supersededId) {
    return byId;
  }

  const { [supersededId]: _superseded, ...rest } = byId;

  return rest;
};

/**
 * Assistant rows REST catch-up would merge that the live store did not already have, excluding the
 * confirmation of an optimistic user send — that is not a missed socket payload. Tool rows still
 * merge via `mergeServerMessages`; only user-visible assistant gaps are reported to analytics.
 */
export const collectMissedWorkspaceAgentCatchUpMessages = (
  currentById: WorkspaceAgentMessagesById,
  serverMessages: AgentSessionMessageResponse[],
): AgentSessionMessageResponse[] =>
  serverMessages.filter((message) => {
    if (message.role !== 'assistant') {
      return false;
    }

    if (currentById[message.id] !== undefined) {
      return false;
    }

    const withoutOptimistic = dropSupersededOptimisticRow(currentById, normalizeWorkspaceAgentMessage(message));

    return Object.keys(withoutOptimistic).length === Object.keys(currentById).length;
  });

/** True when REST catch-up should fold a server row into the live store (new id or a longer assistant reply). */
export const shouldApplyWorkspaceAgentCatchUpServerMessage = (
  current: WorkspaceAgentMessage | undefined,
  serverMessage: AgentSessionMessageResponse,
): boolean => {
  if (current === undefined) {
    return true;
  }

  if (current.isPending) {
    return false;
  }

  const server = normalizeWorkspaceAgentMessage(serverMessage);

  if (server.role === 'toolResult') {
    return workspaceAgentMessageShouldInvalidateCaches(server) && !workspaceAgentMessageShouldInvalidateCaches(current);
  }

  if (server.role !== 'assistant') {
    return false;
  }

  return (server.content ?? '').length > (current.content ?? '').length;
};

export const upsertWorkspaceAgentMessage = (
  byId: WorkspaceAgentMessagesById,
  message: WorkspaceAgentMessage,
): WorkspaceAgentMessagesById => ({ ...dropSupersededOptimisticRow(byId, message), [message.id]: message });

/**
 * Folds in a page of server messages. Superseded rows are dropped here too, because a message that
 * only ever arrives by refetch — the socket having been down when it was written — would otherwise
 * leave its optimistic row pending for the rest of the session.
 */
export const mergeServerWorkspaceAgentMessages = (
  byId: WorkspaceAgentMessagesById,
  messages: AgentSessionMessageResponse[],
): WorkspaceAgentMessagesById =>
  messages.reduce(
    (merged, message) => upsertWorkspaceAgentMessage(merged, normalizeWorkspaceAgentMessage(message)),
    byId,
  );

export const sortWorkspaceAgentMessages = (byId: WorkspaceAgentMessagesById): WorkspaceAgentMessage[] =>
  Object.values(byId).sort((left, right) => left.sequence - right.sequence);
